// frontend/src/App.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  availabilityEndpoint,
  authGoogleStartEndpoint,
  authLoginEndpoint,
  authMeEndpoint,
  authRegisterEndpoint,
  artistProfileEndpoint,
  artistsEndpoint,
  bookingAcceptEndpoint,
  bookingCancelEndpoint,
  bookingDeclineEndpoint,
  bookingReopenEndpoint,
  bookingCounterEndpoint,
  bookingsEndpoint,
  contactMessagesEndpoint,
  contactsIncomingEndpoint,
  contactsMarkReadEndpoint,
  contactsOutgoingEndpoint,
  contactThreadEndpoint,
  contractSignEndpoint,
  DEV_FALLBACK_ENABLED,
  AUTH_STORAGE_KEY,
  gigEndpoint,
  gigInterestedEndpoint,
  gigsEndpoint,
  paymentsDepositEndpoint,
  unreadCountsEndpoint,
  authMeTypeEndpoint,
} from "@/lib/api";
import {
  buildAuthHeaders,
  ensureNumber,
  getGigCity,
  getGigPayout,
  listToInputValue,
  parseList,
  pickRandom,
  sortGigs,
  toISOOrNull,
  toNullableString,
} from "@/lib/utils";
import {
  DEMO_ARTISTS,
  DEMO_GIGS,
  initialAuthFormState,
  initialBookingFormState,
  initialGigFormState,
  initialProfileFormState,
} from "@/lib/constants";

import Header from "@/components/Header";
import AuthForm from "@/components/AuthForm";
import ArtistProfileForm from "@/components/ArtistProfileForm";
import ArtistInbox from "@/components/ArtistInbox";
import GigCreationForm from "@/components/GigCreationForm";
import VenueOutbox from "@/components/VenueOutbox";
import GigBrowseSection from "@/components/GigBrowseSection";
import ArtistBrowseSection from "@/components/ArtistBrowseSection";
import GigDetailsModal from "@/components/modals/GigDetailsModal";
import InterestedArtistsModal from "@/components/modals/InterestedArtistsModal";
import ContactArtistModal from "@/components/modals/ContactArtistModal";
import ArtistProfileModal from "@/components/modals/ArtistProfileModal";
import ConfirmDialog from "@/components/modals/ConfirmDialog";
import ThreadModal from "@/components/modals/ThreadModal";
import ToastContainer from "@/components/ToastContainer";
import NewUserTypeModal from "@/components/NewUserTypeModal";

const App = () => {
  // ── Auth ──────────────────────────────────────────────────────────────────
  const [token, setToken] = useState(() => {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(AUTH_STORAGE_KEY);
  });
  const [currentUser, setCurrentUser] = useState(null);
  const [artistProfile, setArtistProfile] = useState(null);
  const [authMode, setAuthMode] = useState("login");
  const [authForm, setAuthForm] = useState(() => ({ ...initialAuthFormState }));
  const [isSubmittingAuth, setIsSubmittingAuth] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(false);

  // ── Artist profile ────────────────────────────────────────────────────────
  const [profileForm, setProfileForm] = useState(() => ({ ...initialProfileFormState }));
  const [isProfileDirty, setIsProfileDirty] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // ── Gig creation ──────────────────────────────────────────────────────────
  const [gigForm, setGigForm] = useState(() => ({ ...initialGigFormState }));
  const [isCreatingGig, setIsCreatingGig] = useState(false);
  const [editingGig, setEditingGig] = useState(null);

  // ── Gig browsing ──────────────────────────────────────────────────────────
  const [gigs, setGigs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUsingFallback, setIsUsingFallback] = useState(false);
  const [interested, setInterested] = useState([]);
  const [pendingGigId, setPendingGigId] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    city: "all",
    dateFrom: "",
    dateTo: "",
    minPayout: "",
    sort: "date_asc",
  });
  const [activeGigId, setActiveGigId] = useState(null);
  const [activeTab, setActiveTab] = useState("all");

  // ── Artist browsing ───────────────────────────────────────────────────────
  const [artists, setArtists] = useState([]);
  const [isArtistsLoading, setIsArtistsLoading] = useState(false);
  const [artistSearch, setArtistSearch] = useState("");
  const [artistGenreFilter, setArtistGenreFilter] = useState("");
  const [activeArtistContact, setActiveArtistContact] = useState(null);
  const [contactMessage, setContactMessage] = useState("");
  const [isSendingContact, setIsSendingContact] = useState(false);
  const [viewingArtistProfile, setViewingArtistProfile] = useState(null);

  // ── Contacts / messaging ──────────────────────────────────────────────────
  const [contactRequests, setContactRequests] = useState([]);
  const [isContactsLoading, setIsContactsLoading] = useState(false);
  const [sentContacts, setSentContacts] = useState([]);
  const [isSentLoading, setIsSentLoading] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [peerNameCache, setPeerNameCache] = useState({});

  // ── Thread modal ──────────────────────────────────────────────────────────
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [isThreadLoading, setIsThreadLoading] = useState(false);
  const [threadDraft, setThreadDraft] = useState("");
  const [isPostingThreadMessage, setIsPostingThreadMessage] = useState(false);
  const threadEtagRef = useRef(null);
  const [threadBooking, setThreadBooking] = useState(null);
  const [threadRequest, setThreadRequest] = useState(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingForm, setBookingForm] = useState(() => ({ ...initialBookingFormState }));
  const [showCounterForm, setShowCounterForm] = useState(false);
  const [counterForm, setCounterForm] = useState(() => ({ ...initialBookingFormState }));
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [isBookingAction, setIsBookingAction] = useState(false);

  // ── Interested artists modal ──────────────────────────────────────────────
  const [interestedModalGig, setInterestedModalGig] = useState(null);
  const [interestedModalArtists, setInterestedModalArtists] = useState([]);
  const [isInterestedModalLoading, setIsInterestedModalLoading] = useState(false);

  // ── UI ────────────────────────────────────────────────────────────────────
  const [activeView, setActiveView] = useState("gigs");
  const [isClient, setIsClient] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showNewUserTypeModal, setShowNewUserTypeModal] = useState(false);
  const [theme, setTheme] = useState(() => {
    if (typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches) {
      return "dark";
    }
    return "dark"; // default to dark theme
  });

  // ── Toast helpers ─────────────────────────────────────────────────────────
  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback(
    (type, message) => {
      if (!message) return;
      const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      setToasts((prev) => [...prev, { id, type, message }]);
      if (typeof window !== "undefined") {
        window.setTimeout(() => removeToast(id), 4000);
      }
    },
    [removeToast]
  );

  // ── Auth helpers ──────────────────────────────────────────────────────────
  const handleAuthFailure = useCallback(
    (message) => {
      setToken(null);
      setCurrentUser(null);
      setArtistProfile(null);
      setInterested([]);
      setAuthForm(() => ({ ...initialAuthFormState }));
      setAuthMode("login");
      setProfileForm(() => ({ ...initialProfileFormState }));
      setIsProfileDirty(false);
      setIsProfileLoading(false);
      setIsAuthLoading(false);
      setIsSavingProfile(false);
      setIsSubmittingAuth(false);
      setIsCreatingGig(false);
      setGigForm(() => ({ ...initialGigFormState }));
      if (typeof message === "string") {
        const trimmed = message.trim();
        if (trimmed.length > 0) addToast("info", trimmed);
      }
    },
    [addToast]
  );

  const handleLogout = useCallback(() => {
    handleAuthFailure("You have been signed out.");
  }, [handleAuthFailure]);

  const handleAuthInputChange = useCallback(
    (field) => (event) => {
      const value = event?.target?.value ?? "";
      setAuthForm((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const switchAuthMode = useCallback((mode) => {
    setAuthMode(mode);
    setAuthForm((prev) => ({ ...prev, password: "", confirmPassword: "" }));
    setShowAuthModal(true);
  }, []);

  const handleGoogleLogin = useCallback(() => {
    if (typeof window === "undefined") return;
    const next = `${window.location.pathname}${window.location.search}`;
    const params = new URLSearchParams();
    if (authMode === "register") {
      params.set("user_type", authForm.userType || "artist");
    }
    if (next) params.set("next", next);
    const target = params.toString()
      ? `${authGoogleStartEndpoint}?${params.toString()}`
      : authGoogleStartEndpoint;
    window.location.assign(target);
  }, [authForm.userType, authMode]);

  const handleSetUserType = useCallback(
    async (userType) => {
      try {
        const response = await fetch(authMeTypeEndpoint, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", ...buildAuthHeaders(token) },
          body: JSON.stringify({ user_type: userType }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data?.detail || "Unable to set account type.");
        }
        setCurrentUser(data);
        setShowNewUserTypeModal(false);
        const label = userType.charAt(0).toUpperCase() + userType.slice(1);
        addToast("success", `Account set up as ${label}`);
      } catch (err) {
        addToast("error", err.message || "Something went wrong. Please try again.");
      }
    },
    [token, addToast],
  );

  // ── Profile helpers ───────────────────────────────────────────────────────
  const handleProfileFieldChange = useCallback(
    (field) => (event) => {
      const value = event?.target?.value ?? "";
      setProfileForm((prev) => ({ ...prev, [field]: value }));
      setIsProfileDirty(true);
    },
    []
  );

  // ── Gig form helpers ──────────────────────────────────────────────────────
  const handleGigFieldChange = useCallback(
    (field) => (event) => {
      const value = event?.target?.value ?? "";
      setGigForm((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const resetGigForm = useCallback(() => {
    setGigForm(() => ({ ...initialGigFormState }));
  }, []);

  // ── Theme ─────────────────────────────────────────────────────────────────
  const handleThemeToggle = useCallback(() => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  // Close auth modal on successful authentication
  useEffect(() => {
    if (token && currentUser) setShowAuthModal(false);
  }, [token, currentUser]);

  // ── Google OAuth token from URL hash ──────────────────────────────────────
  useEffect(() => {
    if (typeof window === "undefined" || !window.location.hash) return;
    const params = new URLSearchParams(window.location.hash.slice(1));
    const incomingToken = params.get("token") || params.get("access_token");
    if (!incomingToken) return;
    setToken(incomingToken);
    if (params.get("new") === "1") {
      setShowNewUserTypeModal(true);
      addToast("success", "Welcome to Onetrack! Choose your account type.");
    } else {
      addToast("success", "Signed in with Google.");
    }
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
  }, [addToast]);

  // ── Stripe payment redirect ───────────────────────────────────────────────
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get("payment");
    const bookingId = params.get("booking_id");
    if (!paymentStatus) return;

    window.history.replaceState(null, "", window.location.pathname);

    if (paymentStatus === "success" && bookingId && token) {
      addToast("success", "Payment received! Your booking is confirmed.");
      fetch(`${bookingsEndpoint}/${bookingId}`, { headers: buildAuthHeaders(token) })
        .then((r) => r.ok ? r.json() : null)
        .then((booking) => { if (booking) setThreadBooking(booking); })
        .catch(() => {});
    } else if (paymentStatus === "cancel") {
      addToast("info", "Payment cancelled. You can try again from the booking thread.");
    }
  }, [token, addToast]);

  // ── Derived user flags ────────────────────────────────────────────────────
  const normalizedUserType = currentUser?.user_type?.toLowerCase() ?? null;
  const isArtistUser = normalizedUserType === "artist";
  const isVenueUser = normalizedUserType === "venue";
  const isAuthenticated = Boolean(token && currentUser);
  const canViewAvailability = isUsingFallback || isArtistUser;
  const canManageAvailability = isArtistUser && Boolean(token);

  // ── Profile computed ──────────────────────────────────────────────────────
  const profileGenresList = useMemo(() => parseList(profileForm.genres), [profileForm.genres]);
  const profileInstrumentsList = useMemo(() => parseList(profileForm.instruments), [profileForm.instruments]);
  const profileLinksList = useMemo(() => parseList(profileForm.socialLinks), [profileForm.socialLinks]);

  // ── Gig filtering ─────────────────────────────────────────────────────────
  const filteredGigs = useMemo(() => {
    const normalizeDate = (value) => {
      if (!value) return null;
      const date = new Date(value);
      const ms = date.getTime();
      return Number.isNaN(ms) ? null : date;
    };
    const dateFrom = filters.dateFrom ? normalizeDate(filters.dateFrom) : null;
    const dateTo = filters.dateTo ? normalizeDate(filters.dateTo) : null;
    const applyFilters = gigs.filter((gig) => {
      const title = gig?.title?.toLowerCase() ?? "";
      const searchTerm = filters.search.trim().toLowerCase();
      if (searchTerm && !title.includes(searchTerm)) return false;
      const gigCity = getGigCity(gig);
      if (filters.city !== "all" && filters.city && gigCity.toLowerCase() !== filters.city.toLowerCase()) return false;
      const gigDate = gig?.date ? normalizeDate(gig.date) : null;
      if (dateFrom && (!gigDate || gigDate < dateFrom)) return false;
      if (dateTo && gigDate) {
        const endOfDay = new Date(dateTo);
        endOfDay.setHours(23, 59, 59, 999);
        if (gigDate > endOfDay) return false;
      }
      const minPayout = filters.minPayout ? Number(filters.minPayout) : null;
      if (minPayout && minPayout > 0) {
        const payout = getGigPayout(gig);
        if (!Number.isFinite(payout) || payout < minPayout) return false;
      }
      return true;
    });
    const sorted = [...applyFilters];
    const compareByDate = (a, b) => {
      const msA = a?.date ? new Date(a.date).getTime() : NaN;
      const msB = b?.date ? new Date(b.date).getTime() : NaN;
      return (Number.isNaN(msA) ? Infinity : msA) - (Number.isNaN(msB) ? Infinity : msB);
    };
    const compareByPayout = (a, b) => {
      const pA = getGigPayout(a);
      const pB = getGigPayout(b);
      if (!Number.isFinite(pA) && !Number.isFinite(pB)) return 0;
      if (!Number.isFinite(pA)) return 1;
      if (!Number.isFinite(pB)) return -1;
      return pA - pB;
    };
    switch (filters.sort) {
      case "date_desc": sorted.sort((a, b) => compareByDate(b, a)); break;
      case "payout_desc": sorted.sort((a, b) => compareByPayout(b, a)); break;
      case "payout_asc": sorted.sort(compareByPayout); break;
      default: sorted.sort(compareByDate); break;
    }
    return sorted;
  }, [filters, gigs]);

  const visibleGigs = useMemo(() => {
    if (isVenueUser && currentUser) {
      return filteredGigs.filter((gig) => String(gig.venue_id) === String(currentUser.id));
    }
    return filteredGigs;
  }, [filteredGigs, isVenueUser, currentUser]);

  const summary = useMemo(() => {
    const gigPool = isVenueUser ? visibleGigs : gigs;
    const total = gigPool.length;
    const interestedCount = isArtistUser ? interested.length : 0;
    const upcomingDates = gigPool
      .map((gig) => (gig.date ? new Date(gig.date) : null))
      .filter((date) => date instanceof Date && !Number.isNaN(date));
    const nextGigDate = upcomingDates.length
      ? new Date(Math.min(...upcomingDates.map((d) => d.getTime())))
      : null;
    return {
      total,
      interested: interestedCount,
      nextGigDisplay: nextGigDate
        ? nextGigDate.toLocaleDateString(undefined, { dateStyle: "medium" })
        : null,
    };
  }, [gigs, interested, isArtistUser, isVenueUser, visibleGigs]);

  const cityOptions = useMemo(() => {
    const unique = new Set();
    gigs.forEach((gig) => {
      const city = getGigCity(gig);
      if (city) unique.add(city);
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [gigs]);

  const interestedIds = useMemo(() => new Set(interested.map((id) => String(id))), [interested]);

  const myAvailabilityGigs = useMemo(() => {
    if (!canViewAvailability) return [];
    return filteredGigs.filter((gig) => interestedIds.has(String(gig.id)));
  }, [filteredGigs, interestedIds, canViewAvailability]);

  const displayedGigs = activeTab === "mine" ? myAvailabilityGigs : visibleGigs;

  const filteredArtists = useMemo(() => {
    const term = artistSearch.trim().toLowerCase();
    if (!term) return artists;
    return artists.filter((artist) => {
      const fields = [
        artist.name, artist.username, artist.bio, artist.experience_level, artist.website,
        ...(artist.genres || []), ...(artist.instruments || []),
      ].filter(Boolean).map((v) => `${v}`.toLowerCase());
      return fields.some((v) => v.includes(term));
    });
  }, [artists, artistSearch]);

  const showGigInterface = !isVenueUser || activeView === "gigs";
  const showArtistInterface = isVenueUser && activeView === "artists";

  const outgoingUnread = useMemo(() => {
    const fromCounts = unreadCounts?.outgoing ?? unreadCounts?.venue ?? unreadCounts?.totalOutgoing;
    if (typeof fromCounts === "number") return fromCounts;
    return 0;
  }, [unreadCounts]);

  const getContactPeerLabel = useCallback(
    (c) => {
      const peerId = c?.artist_id ?? c?.venue_id;
      const role = c?.artist_id ? "artist" : "venue";
      const cached = peerNameCache[peerId];
      return cached ? `${cached}` : `${role} #${peerId}`;
    },
    [peerNameCache]
  );

  // ── Active gig derived ────────────────────────────────────────────────────
  const activeGig = useMemo(() => {
    if (activeGigId == null) return null;
    return (
      gigs.find((gig) => String(gig.id) === String(activeGigId)) ||
      filteredGigs.find((gig) => String(gig.id) === String(activeGigId)) ||
      null
    );
  }, [activeGigId, gigs, filteredGigs]);

  const activeCity = activeGig ? getGigCity(activeGig) : "";
  const activePayout = activeGig ? getGigPayout(activeGig) : null;
  const activeGenres = activeGig ? parseList(activeGig.genres) : [];
  const activeTags = activeGig ? parseList(activeGig.tags) : [];
  const activeDescription = activeGig?.description?.trim() ?? "";
  const activeVenueName = activeGig?.venue_name || activeGig?.venueName || activeGig?.venue?.name || "";
  const activeContactName = activeGig?.contact_name || activeGig?.contactName || activeGig?.contact?.name || "";
  const activeContactEmail = activeGig?.contact_email || activeGig?.contactEmail || activeGig?.contact?.email || "";
  const activeContactPhone = activeGig?.contact_phone || activeGig?.contactPhone || activeGig?.contact?.phone || "";
  const activeWebsite = activeGig?.website || activeGig?.link || "";
  const mapsQueryParts = activeGig
    ? [activeVenueName, activeGig?.address, activeCity].map((p) => `${p ?? ""}`.trim()).filter(Boolean)
    : [];
  const mapsUrl = mapsQueryParts.length
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapsQueryParts.join(", "))}`
    : "";
  const isActiveGigInterested = activeGig ? interestedIds.has(String(activeGig.id)) : false;
  const modalCanRespond = canManageAvailability || isUsingFallback;
  const modalShowInterestedButton = isVenueUser && !modalCanRespond;
  const modalAvailabilityDisabled = modalShowInterestedButton
    ? false
    : pendingGigId === activeGig?.id || !modalCanRespond;
  const modalAvailabilityLabel = modalShowInterestedButton
    ? "Interested artists"
    : !modalCanRespond
      ? "Log in to respond"
      : pendingGigId === activeGig?.id
        ? "Saving\u2026"
        : isActiveGigInterested
          ? "Unmark Availability"
          : "I'm Available";
  const modalAvailabilityClass = modalAvailabilityDisabled
    ? "btn-secondary cursor-not-allowed opacity-60"
    : modalShowInterestedButton
      ? "btn-secondary"
      : isActiveGigInterested
        ? "btn-secondary"
        : "btn-primary";

  // ── Booking derived ───────────────────────────────────────────────────────
  const bookingSignatures = threadBooking?.contract?.signatures || [];
  const venueSigned = bookingSignatures.some((sig) => sig.signer_role === "venue");
  const artistSigned = bookingSignatures.some((sig) => sig.signer_role === "artist");
  const bookingPayments = threadBooking?.payments || [];
  const depositPaid = bookingPayments.some((payment) => payment.status === "paid");
  const depositAmount = threadBooking?.contract?.terms?.deposit_amount;
  const canCreateBooking = isVenueUser && !threadBooking && Boolean(threadRequest);
  const canAcceptBooking = isArtistUser && threadBooking?.status === "offered";
  const canDeclineBooking = isArtistUser && threadBooking?.status === "offered";
  const canCancelBooking = Boolean(threadBooking) && !["cancelled", "declined"].includes(threadBooking?.status);
  const canReopenBooking = isVenueUser && threadBooking?.status === "declined";
  const canCounterOffer = Boolean(threadBooking) && ["offered", "accepted"].includes(threadBooking?.status);
  const canSignContract =
    Boolean(threadBooking?.contract) &&
    ((isVenueUser && !venueSigned) || (isArtistUser && !artistSigned)) &&
    ["offered", "accepted", "signed"].includes(threadBooking?.status || "");
  const canPayDeposit =
    isVenueUser &&
    threadBooking?.status === "signed" &&
    depositAmount != null &&
    !depositPaid;

  // ── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (token) window.localStorage.setItem(AUTH_STORAGE_KEY, token);
    else window.localStorage.removeItem(AUTH_STORAGE_KEY);
  }, [token]);

  useEffect(() => {
    if (!token) {
      setCurrentUser(null);
      setArtistProfile(null);
      setInterested([]);
      setContactRequests([]);
      setProfileForm(() => ({ ...initialProfileFormState }));
      setIsProfileDirty(false);
      setIsAuthLoading(false);
      return;
    }
    let isMounted = true;
    setIsAuthLoading(true);
    const fetchCurrentUser = async () => {
      try {
        const response = await fetch(authMeEndpoint, { headers: buildAuthHeaders(token) });
        if (!response.ok) {
          if (response.status === 401) {
            if (isMounted) handleAuthFailure("Session expired. Please log in again.");
            return;
          }
          throw new Error(`Failed to load user: ${response.status}`);
        }
        const data = await response.json();
        if (isMounted) setCurrentUser(data);
      } catch (err) {
        if (isMounted) {
          console.error("Error fetching user:", err);
          handleAuthFailure("Unable to verify your session. Please log in again.");
        }
      } finally {
        if (isMounted) setIsAuthLoading(false);
      }
    };
    fetchCurrentUser();
    return () => { isMounted = false; };
  }, [token, handleAuthFailure]);

  useEffect(() => {
    if (!canViewAvailability && activeTab === "mine") setActiveTab("all");
  }, [canViewAvailability, activeTab]);

  useEffect(() => {
    if (!token || !isArtistUser) {
      setArtistProfile(null);
      setContactRequests([]);
      setProfileForm(() => ({ ...initialProfileFormState }));
      setIsProfileDirty(false);
      setIsProfileLoading(false);
      return;
    }
    let isMounted = true;
    setIsProfileLoading(true);
    const fetchProfile = async () => {
      try {
        const response = await fetch(artistProfileEndpoint, { headers: buildAuthHeaders(token) });
        if (!response.ok) {
          if (response.status === 401) {
            if (isMounted) handleAuthFailure("Session expired. Please log in again.");
            return;
          }
          throw new Error(`Failed to load profile: ${response.status}`);
        }
        const data = await response.json();
        if (isMounted) {
          setArtistProfile(data);
          setProfileForm({
            bio: data.bio || "",
            experienceLevel: data.experience_level || "",
            website: data.website || "",
            genres: listToInputValue(data.genres),
            instruments: listToInputValue(data.instruments),
            socialLinks: listToInputValue(data.social_links),
            coverImageUrl: data.cover_image_url || "",
          });
          setIsProfileDirty(false);
        }
      } catch (err) {
        if (isMounted) {
          console.error("Error loading profile:", err);
          addToast("error", "Couldn't load your artist profile.");
        }
      } finally {
        if (isMounted) setIsProfileLoading(false);
      }
    };
    fetchProfile();
    return () => { isMounted = false; };
  }, [token, isArtistUser, addToast, handleAuthFailure]);

  useEffect(() => {
    if (!token || !isArtistUser) {
      setContactRequests([]);
      setIsContactsLoading(false);
      return;
    }
    let isMounted = true;
    const loadContacts = async () => {
      setIsContactsLoading(true);
      try {
        const response = await fetch(contactsIncomingEndpoint, { headers: buildAuthHeaders(token) });
        if (!response.ok) {
          if (response.status === 401) { handleAuthFailure("Session expired. Please log in again."); return; }
          throw new Error(`Failed to load inbox: ${response.status}`);
        }
        const data = await response.json();
        if (isMounted) setContactRequests(Array.isArray(data) ? data : []);
      } catch (err) {
        if (isMounted) { console.error("Inbox load error:", err); addToast("error", "Couldn't load messages."); }
      } finally {
        if (isMounted) setIsContactsLoading(false);
      }
    };
    loadContacts();
    return () => { isMounted = false; };
  }, [token, isArtistUser, addToast, handleAuthFailure]);

  useEffect(() => {
    if (!token || !isVenueUser) {
      setSentContacts([]);
      setIsSentLoading(false);
      return;
    }
    let isMounted = true;
    const loadSent = async () => {
      setIsSentLoading(true);
      try {
        const response = await fetch(contactsOutgoingEndpoint, { headers: buildAuthHeaders(token) });
        if (!response.ok) {
          if (response.status === 401) { handleAuthFailure("Session expired. Please log in again."); return; }
          throw new Error(`Failed to load sent: ${response.status}`);
        }
        const data = await response.json();
        if (isMounted) setSentContacts(Array.isArray(data) ? data : []);
      } catch (err) {
        if (isMounted) { console.error("Outbox load error:", err); addToast("error", "Couldn't load sent messages."); }
      } finally {
        if (isMounted) setIsSentLoading(false);
      }
    };
    loadSent();
    return () => { isMounted = false; };
  }, [token, isVenueUser, addToast, handleAuthFailure]);

  useEffect(() => {
    if (!token) { setUnreadCounts({}); return; }
    let isMounted = true;
    const loadUnread = async () => {
      try {
        const res = await fetch(unreadCountsEndpoint, { headers: buildAuthHeaders(token) });
        if (!res.ok) return;
        const data = await res.json().catch(() => ({}));
        if (isMounted && data && typeof data === "object") setUnreadCounts(data);
      } catch (e) { void e; }
    };
    loadUnread();
    const t = window.setInterval(loadUnread, 15000);
    return () => { isMounted = false; window.clearInterval(t); };
  }, [token]);

  useEffect(() => {
    let isMounted = true;
    const fetchGigs = async () => {
      try {
        const response = await fetch(gigsEndpoint, { headers: buildAuthHeaders(token) });
        if (!response.ok) throw new Error(`Failed to fetch gigs: ${response.status}`);
        const data = await response.json();
        if (!isMounted) return;
        if (Array.isArray(data) && data.length > 0) {
          setGigs(sortGigs(data));
          setIsUsingFallback(false);
          return;
        }
        if (DEV_FALLBACK_ENABLED) {
          setGigs(sortGigs(DEMO_GIGS));
          setIsUsingFallback(true);
        } else {
          setGigs([]);
          setIsUsingFallback(false);
        }
      } catch (err) {
        console.error("Error fetching gigs:", err);
        if (isMounted) {
          if (DEV_FALLBACK_ENABLED) {
            setGigs(sortGigs(DEMO_GIGS));
            setIsUsingFallback(true);
            addToast("info", "Unable to reach the API. Showing demo gigs for now.");
          } else {
            addToast("error", "Unable to load gigs right now. Please try again later.");
          }
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    fetchGigs();
    return () => { isMounted = false; };
  }, [addToast, token]);

  useEffect(() => {
    if (isUsingFallback || !token || !isArtistUser || gigs.length === 0) {
      if (!token || !isArtistUser) setInterested([]);
      return;
    }
    let isMounted = true;
    const fetchAvailability = async () => {
      try {
        const response = await fetch(availabilityEndpoint, { headers: buildAuthHeaders(token) });
        if (!response.ok) {
          if (response.status === 401) {
            if (isMounted) handleAuthFailure("Session expired. Please log in again.");
            return;
          }
          throw new Error(`Failed to load availability: ${response.status}`);
        }
        const data = await response.json();
        if (isMounted && Array.isArray(data)) {
          setInterested(data.map((e) => Number(e?.gig_id)).filter((id) => Number.isFinite(id)));
        }
      } catch (err) {
        if (isMounted) { console.error("Error fetching availability:", err); addToast("error", "Unable to load your availability right now."); }
      }
    };
    fetchAvailability();
    return () => { isMounted = false; };
  }, [addToast, gigs.length, handleAuthFailure, isArtistUser, isUsingFallback, token]);

  const artistsAbortRef = useRef(null);
  useEffect(() => {
    if (!token || !isVenueUser) {
      setArtists(pickRandom(DEMO_ARTISTS, 9));
      return undefined;
    }
    const controller = new AbortController();
    if (artistsAbortRef.current) {
      try { artistsAbortRef.current.abort(); } catch { /* ignore */ }
    }
    artistsAbortRef.current = controller;
    const term = artistGenreFilter.trim();
    setIsArtistsLoading(true);
    const handle = window.setTimeout(async () => {
      try {
        const genreQuery = term ? `?genre=${encodeURIComponent(term)}` : "";
        const response = await fetch(`${artistsEndpoint}${genreQuery}`, {
          headers: buildAuthHeaders(token),
          signal: controller.signal,
        });
        if (!response.ok) {
          setArtists(term ? [] : pickRandom(DEMO_ARTISTS, 9));
          if (response.status >= 500) console.error("Artist load failed:", response.status);
          return;
        }
        const data = await response.json().catch(() => []);
        const list = Array.isArray(data) ? data : [];
        setArtists(list.length === 0 && !term ? pickRandom(DEMO_ARTISTS, 9) : list);
      } catch (error) {
        if (error?.name === "AbortError") return;
        console.error("Error fetching artists:", error);
        setArtists(term ? [] : pickRandom(DEMO_ARTISTS, 9));
      } finally {
        setIsArtistsLoading(false);
      }
    }, 400);
    return () => {
      window.clearTimeout(handle);
      try { controller.abort(); } catch { /* ignore */ }
    };
  }, [isVenueUser, token, artistGenreFilter]);

  useEffect(() => {
    setActiveView(isVenueUser ? "artists" : "gigs");
  }, [isVenueUser]);

  // Gig modal effects
  useEffect(() => {
    if (activeGigId == null) return;
    const exists = gigs.some((gig) => String(gig.id) === String(activeGigId));
    if (!exists) setActiveGigId(null);
  }, [activeGigId, gigs]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (activeGig) {
      const previous = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = previous; };
    }
    document.body.style.overflow = "";
    return undefined;
  }, [activeGig]);

  useEffect(() => { setIsClient(true); }, []);

  useEffect(() => {
    if (!activeGig || typeof window === "undefined") return undefined;
    const handleKey = (event) => {
      if (event.key === "Escape") setActiveGigId(null);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [activeGig]);

  // ── Thread ────────────────────────────────────────────────────────────────
  const fetchThreadMessages = useCallback(async (contactId, { setLoading = false } = {}) => {
    if (!contactId || !token) return;
    if (setLoading) setIsThreadLoading(true);
    try {
      const headers = buildAuthHeaders(token);
      if (threadEtagRef.current) headers["If-None-Match"] = threadEtagRef.current;
      const res = await fetch(contactThreadEndpoint(contactId), { headers });
      if (res.status === 304) return;
      if (!res.ok) {
        if (res.status === 401) { handleAuthFailure("Session expired. Please log in again."); return; }
        throw new Error(`Failed to load thread: ${res.status}`);
      }
      const data = await res.json().catch(() => ({}));
      setThreadMessages(Array.isArray(data?.messages) ? data.messages : []);
      setThreadBooking(data?.booking || null);
      setThreadRequest(data?.request || null);
      const newTag = res.headers.get("ETag");
      if (newTag) threadEtagRef.current = newTag;
      const peerId = data?.peer_id ?? data?.request?.artist_id ?? data?.request?.venue_id;
      const peerName = data?.peer_name;
      if (peerId && peerName) setPeerNameCache((p) => ({ ...p, [peerId]: peerName }));
    } catch (_e) {
      console.error("Thread load error:", _e);
      addToast("error", _e.message || "Could not open conversation.");
      setActiveThreadId(null);
    } finally {
      if (setLoading) setIsThreadLoading(false);
    }
  }, [token, addToast, handleAuthFailure]);

  const openThread = useCallback(async (contact) => {
    if (!contact || !token) return;
    const contactId = contact.id ?? contact.contact_id ?? contact;
    setActiveThreadId(contactId);
    setThreadMessages([]);
    setThreadBooking(null);
    setThreadRequest(null);
    setShowBookingForm(false);
    setBookingForm({ ...initialBookingFormState });
    threadEtagRef.current = null;
    await fetchThreadMessages(contactId, { setLoading: true });
  }, [token, fetchThreadMessages]);

  const closeThread = useCallback(() => {
    setActiveThreadId(null);
    setThreadMessages([]);
    setThreadDraft("");
    setIsPostingThreadMessage(false);
    setThreadBooking(null);
    setThreadRequest(null);
    setShowBookingForm(false);
    setBookingForm({ ...initialBookingFormState });
    setShowCounterForm(false);
    setCounterForm({ ...initialBookingFormState });
    threadEtagRef.current = null;
  }, []);

  useEffect(() => {
    if (!activeThreadId || !token) return undefined;
    const poll = () => {
      if (typeof document !== "undefined" && document.hidden) return;
      fetchThreadMessages(activeThreadId);
    };
    poll();
    const intervalId = window.setInterval(poll, 5000);
    const handleFocus = () => poll();
    window.addEventListener("focus", handleFocus);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
    };
  }, [activeThreadId, token, fetchThreadMessages]);

  const refreshActiveThread = useCallback(() => {
    if (!activeThreadId) return;
    fetchThreadMessages(activeThreadId);
  }, [activeThreadId, fetchThreadMessages]);

  const sendThreadMessage = useCallback(async () => {
    if (!activeThreadId || !token) return;
    const body = threadDraft.trim();
    if (!body) return;
    setIsPostingThreadMessage(true);
    try {
      const res = await fetch(contactMessagesEndpoint(activeThreadId), {
        method: "POST",
        headers: buildAuthHeaders(token, { "Content-Type": "application/json" }),
        body: JSON.stringify({ message: body }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 401) { handleAuthFailure("Session expired. Please log in again."); return; }
        throw new Error(data?.detail || "Failed to send message");
      }
      if (data && data.id) setThreadMessages((prev) => [...prev, data]);
      setThreadDraft("");
      try {
        fetch(unreadCountsEndpoint, { headers: buildAuthHeaders(token) })
          .then((r) => r.json())
          .then((d) => setUnreadCounts(d || {}));
      } catch { /* ignore */ }
    } catch (_e) {
      console.error("Send message error:", _e);
      addToast("error", _e.message || "Unable to send");
    } finally {
      setIsPostingThreadMessage(false);
    }
  }, [activeThreadId, token, threadDraft, addToast, handleAuthFailure]);

  const handleBookingFieldChange = useCallback((field) => (event) => {
    const value = event.target.value;
    setBookingForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const createBookingOffer = useCallback(async () => {
    if (!token || !isVenueUser || !threadRequest) return;
    const title = bookingForm.title.trim();
    if (!title) { addToast("error", "Please add a title for the booking."); return; }
    if (!bookingForm.eventDate) { addToast("error", "Please choose a date and time."); return; }
    const eventDate = new Date(bookingForm.eventDate);
    if (Number.isNaN(eventDate.getTime())) { addToast("error", "Invalid date/time."); return; }
    const payoutValue = bookingForm.payout === "" ? null : Number(bookingForm.payout);
    if (bookingForm.payout !== "" && Number.isNaN(payoutValue)) { addToast("error", "Payout must be a number."); return; }
    const depositValue = bookingForm.depositAmount === "" ? null : Number(bookingForm.depositAmount);
    if (bookingForm.depositAmount !== "" && Number.isNaN(depositValue)) { addToast("error", "Deposit must be a number."); return; }
    const terms = {};
    const notes = toNullableString(bookingForm.notes);
    const cancellationPolicy = toNullableString(bookingForm.cancellationPolicy);
    const techRequirements = toNullableString(bookingForm.techRequirements);
    if (notes) terms.notes = notes;
    if (cancellationPolicy) terms.cancellation_policy = cancellationPolicy;
    if (techRequirements) terms.tech_requirements = techRequirements;
    if (depositValue != null) terms.deposit_amount = depositValue;
    const payload = {
      title,
      event_date: eventDate.toISOString(),
      city: toNullableString(bookingForm.city),
      payout: payoutValue,
      currency: toNullableString(bookingForm.currency) || "DKK",
      artist_id: threadRequest.artist_id,
      contact_id: threadRequest.id,
      terms,
    };
    setIsBookingAction(true);
    try {
      const res = await fetch(bookingsEndpoint, {
        method: "POST",
        headers: buildAuthHeaders(token, { "Content-Type": "application/json" }),
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 401) { handleAuthFailure("Session expired. Please log in again."); return; }
        throw new Error(data?.detail || "Unable to create booking.");
      }
      setShowBookingForm(false);
      setBookingForm({ ...initialBookingFormState });
      setThreadBooking(data || null);
      refreshActiveThread();
      addToast("success", "Booking offer sent.");
    } catch (err) {
      console.error("Create booking error:", err);
      addToast("error", err.message || "Unable to create booking.");
    } finally {
      setIsBookingAction(false);
    }
  }, [token, isVenueUser, threadRequest, bookingForm, addToast, handleAuthFailure, refreshActiveThread]);

  const executeBookingAction = useCallback(async (action) => {
    if (!token || !threadBooking) return;
    const endpoint = action === "accept"
      ? bookingAcceptEndpoint(threadBooking.id)
      : action === "decline"
        ? bookingDeclineEndpoint(threadBooking.id)
        : action === "reopen"
          ? bookingReopenEndpoint(threadBooking.id)
          : bookingCancelEndpoint(threadBooking.id);
    setIsBookingAction(true);
    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: buildAuthHeaders(token, { "Content-Type": "application/json" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 401) { handleAuthFailure("Session expired. Please log in again."); return; }
        throw new Error(data?.detail || "Unable to update booking.");
      }
      setThreadBooking(data || null);
      refreshActiveThread();
      addToast("success", "Booking updated.");
    } catch (err) {
      console.error("Booking update error:", err);
      addToast("error", err.message || "Unable to update booking.");
    } finally {
      setIsBookingAction(false);
    }
  }, [token, threadBooking, addToast, handleAuthFailure, refreshActiveThread]);

  const respondToBooking = useCallback((action) => {
    if (action === "cancel") {
      const isSigned = threadBooking?.status === "signed";
      setConfirmDialog({
        message: isSigned
          ? "Are you sure you want to cancel this booking? The contract has already been signed by both parties."
          : "Are you sure you want to cancel this booking?",
        onConfirm: () => executeBookingAction("cancel"),
        confirmLabel: "Cancel booking",
        isDanger: true,
      });
      return;
    }
    if (action === "decline") {
      setConfirmDialog({
        message: "Are you sure you want to decline this offer?",
        onConfirm: () => executeBookingAction("decline"),
        confirmLabel: "Decline offer",
        isDanger: true,
      });
      return;
    }
    executeBookingAction(action);
  }, [executeBookingAction, threadBooking]);

  const executeSignContract = useCallback(async () => {
    if (!token || !threadBooking?.contract?.id) return;
    setIsBookingAction(true);
    try {
      const res = await fetch(contractSignEndpoint(threadBooking.contract.id), {
        method: "POST",
        headers: buildAuthHeaders(token, { "Content-Type": "application/json" }),
        body: JSON.stringify({ signer_name: currentUser?.name || "" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 401) { handleAuthFailure("Session expired. Please log in again."); return; }
        throw new Error(data?.detail || "Unable to sign contract.");
      }
      setThreadBooking(data || null);
      refreshActiveThread();
      addToast("success", "Contract signed.");
    } catch (err) {
      console.error("Contract sign error:", err);
      addToast("error", err.message || "Unable to sign contract.");
    } finally {
      setIsBookingAction(false);
    }
  }, [token, threadBooking, currentUser, addToast, handleAuthFailure, refreshActiveThread]);

  const signBookingContract = useCallback(() => {
    setConfirmDialog({
      message: "Are you sure you want to sign this contract? This is a binding commitment.",
      onConfirm: executeSignContract,
      confirmLabel: "Yes, sign contract",
      isDanger: false,
    });
  }, [executeSignContract]);

  const doOpenCounterForm = useCallback(() => {
    if (!threadBooking) return;
    const eventDateValue = threadBooking.event_date
      ? new Date(threadBooking.event_date).toISOString().slice(0, 16)
      : "";
    setCounterForm({
      title: threadBooking.title || "",
      eventDate: eventDateValue,
      city: threadBooking.city || "",
      payout: threadBooking.payout != null ? String(threadBooking.payout) : "",
      currency: threadBooking.currency || "DKK",
      depositAmount: threadBooking.contract?.terms?.deposit_amount != null
        ? String(threadBooking.contract.terms.deposit_amount) : "",
      notes: threadBooking.contract?.terms?.notes || "",
      cancellationPolicy: threadBooking.contract?.terms?.cancellation_policy || "",
      techRequirements: threadBooking.contract?.terms?.tech_requirements || "",
    });
    setShowCounterForm(true);
  }, [threadBooking]);

  const openCounterForm = useCallback(() => {
    if (!threadBooking) return;
    if (threadBooking.status === "accepted") {
      setConfirmDialog({
        message: "This booking has already been accepted. Sending a counter-offer will re-open negotiation. Are you sure?",
        onConfirm: doOpenCounterForm,
        confirmLabel: "Yes, propose changes",
        isDanger: false,
      });
      return;
    }
    doOpenCounterForm();
  }, [threadBooking, doOpenCounterForm]);

  const handleCounterFieldChange = useCallback((field) => (event) => {
    setCounterForm((prev) => ({ ...prev, [field]: event.target.value }));
  }, []);

  const submitCounterOffer = useCallback(async () => {
    if (!token || !threadBooking) return;
    const title = counterForm.title.trim();
    if (!title) { addToast("error", "Please add a title."); return; }
    if (!counterForm.eventDate) { addToast("error", "Please choose a date and time."); return; }
    const eventDate = new Date(counterForm.eventDate);
    if (Number.isNaN(eventDate.getTime())) { addToast("error", "Invalid date/time."); return; }
    const payoutValue = counterForm.payout === "" ? null : Number(counterForm.payout);
    if (counterForm.payout !== "" && Number.isNaN(payoutValue)) { addToast("error", "Payout must be a number."); return; }
    const depositValue = counterForm.depositAmount === "" ? null : Number(counterForm.depositAmount);
    if (counterForm.depositAmount !== "" && Number.isNaN(depositValue)) { addToast("error", "Deposit must be a number."); return; }
    const terms = {};
    const notes = toNullableString(counterForm.notes);
    const cancellationPolicy = toNullableString(counterForm.cancellationPolicy);
    const techRequirements = toNullableString(counterForm.techRequirements);
    if (notes) terms.notes = notes;
    if (cancellationPolicy) terms.cancellation_policy = cancellationPolicy;
    if (techRequirements) terms.tech_requirements = techRequirements;
    if (depositValue != null) terms.deposit_amount = depositValue;
    const payload = {
      title,
      event_date: eventDate.toISOString(),
      city: toNullableString(counterForm.city),
      payout: payoutValue,
      currency: toNullableString(counterForm.currency) || "DKK",
      terms,
    };
    setIsBookingAction(true);
    try {
      const res = await fetch(bookingCounterEndpoint(threadBooking.id), {
        method: "POST",
        headers: buildAuthHeaders(token, { "Content-Type": "application/json" }),
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 401) { handleAuthFailure("Session expired. Please log in again."); return; }
        throw new Error(data?.detail || "Unable to submit counteroffer.");
      }
      setShowCounterForm(false);
      setCounterForm({ ...initialBookingFormState });
      setThreadBooking(data || null);
      refreshActiveThread();
      addToast("success", "Counteroffer sent.");
    } catch (err) {
      console.error("Counteroffer error:", err);
      addToast("error", err.message || "Unable to submit counteroffer.");
    } finally {
      setIsBookingAction(false);
    }
  }, [token, threadBooking, counterForm, addToast, handleAuthFailure, refreshActiveThread]);

  const createDepositPayment = useCallback(async () => {
    if (!token || !threadBooking) return;
    setIsBookingAction(true);
    try {
      const res = await fetch(paymentsDepositEndpoint, {
        method: "POST",
        headers: buildAuthHeaders(token, { "Content-Type": "application/json" }),
        body: JSON.stringify({ booking_id: threadBooking.id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 401) { handleAuthFailure("Session expired. Please log in again."); return; }
        throw new Error(data?.detail || "Unable to create payment.");
      }
      if (data?.checkout_url) { window.location.href = data.checkout_url; return; }
      addToast("info", "Payment created.");
    } catch (err) {
      console.error("Create payment error:", err);
      addToast("error", err.message || "Unable to create payment.");
    } finally {
      setIsBookingAction(false);
    }
  }, [token, threadBooking, addToast, handleAuthFailure]);

  const handleMarkContactRead = useCallback(async (contactId) => {
    if (!token || !isArtistUser) return;
    try {
      const response = await fetch(contactsMarkReadEndpoint(contactId), {
        method: "POST",
        headers: buildAuthHeaders(token),
      });
      if (!response.ok) {
        if (response.status === 401) { handleAuthFailure("Session expired. Please log in again."); return; }
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || "Unable to update message");
      }
      const updated = await response.json().catch(() => null);
      setContactRequests((prev) =>
        prev.map((c) => (String(c.id) === String(contactId) ? (updated || { ...c, read: true }) : c))
      );
    } catch (err) {
      console.error("Mark read error:", err);
      addToast("error", err.message || "Couldn't update message");
    }
  }, [token, isArtistUser, addToast, handleAuthFailure]);

  const openInterestedModal = useCallback(async (gig) => {
    if (!gig || !token || !isVenueUser) {
      addToast("info", "Sign in as a venue to view interested artists.");
      return;
    }
    setInterestedModalGig(gig);
    setIsInterestedModalLoading(true);
    setInterestedModalArtists([]);
    try {
      const response = await fetch(gigInterestedEndpoint(gig.id), { headers: buildAuthHeaders(token) });
      if (!response.ok) {
        if (response.status === 401) { handleAuthFailure("Session expired. Please log in again."); return; }
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || "Unable to load interested artists");
      }
      const data = await response.json().catch(() => []);
      setInterestedModalArtists(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Interested list error:", err);
      addToast("error", err.message || "Couldn't load interested artists.");
    } finally {
      setIsInterestedModalLoading(false);
    }
  }, [token, isVenueUser, addToast, handleAuthFailure]);

  const closeInterestedModal = useCallback(() => {
    setInterestedModalGig(null);
    setInterestedModalArtists([]);
    setIsInterestedModalLoading(false);
  }, []);

  const handleOpenArtistProfile = useCallback((artist) => {
    setViewingArtistProfile(artist);
  }, []);

  const handleCloseArtistProfile = useCallback(() => {
    setViewingArtistProfile(null);
  }, []);

  const handleOpenArtistContact = useCallback((artist) => {
    setActiveArtistContact(artist);
    setContactMessage("");
  }, []);

  const handleCloseArtistContact = useCallback(() => {
    if (isSendingContact) return;
    setActiveArtistContact(null);
    setContactMessage("");
  }, [isSendingContact]);

  const handleSendArtistContact = useCallback(async () => {
    if (!activeArtistContact || !token) {
      addToast("info", "Sign in as a venue to contact artists.");
      return;
    }
    const trimmed = contactMessage.trim();
    if (!trimmed) { addToast("error", "Please write a short message first."); return; }
    setIsSendingContact(true);
    try {
      const response = await fetch(`${artistsEndpoint}${activeArtistContact.id}/contact`, {
        method: "POST",
        headers: buildAuthHeaders(token, { "Content-Type": "application/json" }),
        body: JSON.stringify({ message: trimmed }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (response.status === 401) { handleAuthFailure("Session expired. Please log in again."); return; }
        throw new Error(data.detail || "Unable to send your message.");
      }
      addToast("success", `Message sent to ${activeArtistContact.name}.`);
      setContactMessage("");
      setActiveArtistContact(null);
    } catch (error) {
      console.error("Contact artist error:", error);
      addToast("error", error.message || "Unable to send your message right now.");
    } finally {
      setIsSendingContact(false);
    }
  }, [activeArtistContact, addToast, contactMessage, handleAuthFailure, token]);

  const performLogin = useCallback(
    async (username, password, successMessage = "Welcome back!") => {
      const response = await fetch(authLoginEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: username.trim(), password }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.detail || (response.status === 401 ? "Invalid username or password." : "Unable to sign in."));
      }
      if (!data?.access_token) throw new Error("Authentication response missing access token.");
      setToken(data.access_token);
      setAuthMode("login");
      addToast("success", successMessage);
      return data;
    },
    [addToast]
  );

  const handleAuthSubmit = async (event) => {
    event.preventDefault();
    if (isSubmittingAuth) return;
    setIsSubmittingAuth(true);
    try {
      if (authMode === "register") {
        const username = authForm.username.trim();
        const name = authForm.name.trim();
        const email = toNullableString(authForm.email);
        const userType = (authForm.userType || "artist").toLowerCase();
        if (username.length < 3) throw new Error("Username must be at least 3 characters long.");
        if (!name) throw new Error("Please provide your display name.");
        if (authForm.password.length < 8) throw new Error("Password must be at least 8 characters long.");
        if (authForm.password !== authForm.confirmPassword) throw new Error("Passwords do not match.");
        if (!["artist", "venue"].includes(userType)) throw new Error("Please choose whether you are an artist or a venue.");
        const payload = { username, password: authForm.password, name, email, user_type: userType };
        const response = await fetch(authRegisterEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.detail || "Unable to create account right now.");
        await performLogin(username, authForm.password, "Account created! You're now signed in.");
        setAuthForm(() => ({ ...initialAuthFormState, userType }));
      } else {
        const username = authForm.username.trim();
        if (!username || !authForm.password) throw new Error("Enter your username and password to continue.");
        await performLogin(username, authForm.password);
        setAuthForm((prev) => ({ ...prev, password: "", confirmPassword: "" }));
      }
    } catch (err) {
      console.error("Authentication error:", err);
      addToast("error", err.message || "Unable to authenticate. Please try again.");
    } finally {
      setIsSubmittingAuth(false);
    }
  };

  const handleProfileSubmit = async (event) => {
    event.preventDefault();
    if (!isArtistUser || !token) { addToast("info", "Sign in as an artist to update your profile."); return; }
    if (!isProfileDirty) { addToast("info", "No profile changes to save."); return; }
    setIsSavingProfile(true);
    try {
      const payload = {
        bio: toNullableString(profileForm.bio),
        experience_level: toNullableString(profileForm.experienceLevel),
        website: toNullableString(profileForm.website),
        genres: parseList(profileForm.genres),
        instruments: parseList(profileForm.instruments),
        social_links: parseList(profileForm.socialLinks),
        cover_image_url: toNullableString(profileForm.coverImageUrl),
      };
      const response = await fetch(artistProfileEndpoint, {
        method: "PUT",
        headers: buildAuthHeaders(token, { "Content-Type": "application/json" }),
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (response.status === 401) { handleAuthFailure("Session expired. Please log in again."); return; }
        throw new Error(data.detail || "Unable to save your profile.");
      }
      setArtistProfile(data);
      setProfileForm({
        bio: data.bio || "",
        experienceLevel: data.experience_level || "",
        website: data.website || "",
        genres: listToInputValue(data.genres),
        instruments: listToInputValue(data.instruments),
        socialLinks: listToInputValue(data.social_links),
        coverImageUrl: data.cover_image_url || "",
      });
      setIsProfileDirty(false);
      addToast("success", "Profile updated.");
    } catch (err) {
      console.error("Profile update error:", err);
      addToast("error", err.message || "Unable to save profile right now.");
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleGigSubmit = async (event) => {
    event.preventDefault();
    if (!isVenueUser || !token) { addToast("info", "Sign in as a venue to post gigs."); return; }
    if (isCreatingGig) return;
    const title = gigForm.title.trim();
    const dateValue = gigForm.date.trim();
    const rawTimeValue = gigForm.time.trim();
    if (!title) { addToast("error", "Please provide a gig title."); return; }
    if (!dateValue || !rawTimeValue) { addToast("error", "Please choose both a date and time for the gig."); return; }
    const [hoursPart = "", minutesPart = ""] = rawTimeValue.split(":");
    const hours = Number(hoursPart);
    const minutes = Number(minutesPart);
    const hasValidTime =
      hoursPart.length > 0 && minutesPart.length === 2 &&
      !Number.isNaN(hours) && !Number.isNaN(minutes) &&
      hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60;
    if (!hasValidTime) { addToast("error", "Use HH:MM for the gig time (e.g., 21:30)."); return; }
    const normalizedTime = `${hoursPart.padStart(2, "0")}:${minutesPart}`;
    const dateIso = toISOOrNull(`${dateValue}T${normalizedTime}:00`);
    if (!dateIso) { addToast("error", "Please choose a valid gig date and time."); return; }
    const payload = {
      title,
      date: dateIso,
      city: toNullableString(gigForm.city),
      payout: ensureNumber(gigForm.payout),
      description: toNullableString(gigForm.description),
      genres: parseList(gigForm.genres),
      tags: parseList(gigForm.tags),
    };
    setIsCreatingGig(true);
    try {
      const isEditing = Boolean(editingGig);
      const response = await fetch(
        isEditing ? gigEndpoint(editingGig.id) : gigsEndpoint,
        {
          method: isEditing ? "PATCH" : "POST",
          headers: buildAuthHeaders(token, { "Content-Type": "application/json" }),
          body: JSON.stringify(payload),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (response.status === 401) { handleAuthFailure("Session expired. Please log in again."); return; }
        throw new Error(data.detail || (isEditing ? "Unable to update gig." : "Unable to create gig right now."));
      }
      setGigs((prev) => sortGigs([data, ...prev.filter((gig) => gig.id !== data.id)]));
      setIsUsingFallback(false);
      resetGigForm();
      setEditingGig(null);
      addToast("success", isEditing ? "Gig updated." : "Gig posted.");
    } catch (err) {
      console.error("Gig submit error:", err);
      addToast("error", err.message || "Unable to save gig right now.");
    } finally {
      setIsCreatingGig(false);
    }
  };

  const handleStartEditGig = useCallback((gig) => {
    const gigDate = gig.date ? new Date(gig.date) : null;
    const dateStr = gigDate
      ? `${gigDate.getFullYear()}-${String(gigDate.getMonth() + 1).padStart(2, "0")}-${String(gigDate.getDate()).padStart(2, "0")}`
      : "";
    const timeStr = gigDate
      ? `${String(gigDate.getHours()).padStart(2, "0")}:${String(gigDate.getMinutes()).padStart(2, "0")}`
      : "";
    setGigForm({
      title: gig.title || "",
      date: dateStr,
      time: timeStr,
      city: gig.city || "",
      payout: gig.payout != null ? String(gig.payout) : "",
      description: gig.description || "",
      genres: (gig.genres || []).join(", "),
      tags: (gig.tags || []).join(", "),
    });
    setEditingGig(gig);
    document.getElementById("gig-form-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const handleCancelEditGig = useCallback(() => {
    setEditingGig(null);
    resetGigForm();
  }, [resetGigForm]);

  const handleDeleteGig = useCallback((gig) => {
    setConfirmDialog({
      message: `Delete "${gig.title}"? This cannot be undone.`,
      onConfirm: async () => {
        try {
          const response = await fetch(gigEndpoint(gig.id), {
            method: "DELETE",
            headers: buildAuthHeaders(token),
          });
          if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(data.detail || "Unable to delete gig.");
          }
          setGigs((prev) => prev.filter((g) => g.id !== gig.id));
          addToast("success", "Gig deleted.");
        } catch (err) {
          addToast("error", err.message || "Unable to delete gig.");
        }
      },
    });
  }, [token, addToast]);

  const handleFilterChange = (key) => (event) => {
    const value = event?.target?.value ?? "";
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({ search: "", city: "all", dateFrom: "", dateTo: "", minPayout: "", sort: "date_asc" });
  };

  const handleToggleAvailability = async (gigId) => {
    const alreadyInterested = interestedIds.has(String(gigId));
    if (!canManageAvailability && !isUsingFallback) {
      addToast("info", "Log in as an artist to manage your availability.");
      return;
    }
    if (isUsingFallback && !canManageAvailability) {
      setInterested((prev) => {
        if (alreadyInterested) { addToast("success", "Availability removed"); return prev.filter((id) => Number(id) !== Number(gigId)); }
        addToast("success", "Availability saved");
        return [...prev, gigId];
      });
      return;
    }
    setPendingGigId(gigId);
    try {
      if (alreadyInterested) {
        const response = await fetch(`${availabilityEndpoint}${gigId}`, {
          method: "DELETE",
          headers: buildAuthHeaders(token),
        });
        if (!response.ok && response.status !== 404) {
          if (response.status === 401) { handleAuthFailure("Session expired. Please log in again."); return; }
          const data = await response.json().catch(() => ({}));
          throw new Error(data.detail || "Failed to update availability");
        }
        setInterested((prev) => prev.filter((id) => Number(id) !== Number(gigId)));
        addToast("success", "Availability removed");
      } else {
        const response = await fetch(availabilityEndpoint, {
          method: "POST",
          headers: buildAuthHeaders(token, { "Content-Type": "application/json" }),
          body: JSON.stringify({ gig_id: gigId }),
        });
        if (!response.ok && response.status !== 409) {
          if (response.status === 401) { handleAuthFailure("Session expired. Please log in again."); return; }
          const data = await response.json().catch(() => ({}));
          throw new Error(data.detail || "Failed to update availability");
        }
        setInterested((prev) => [...new Set([...prev, gigId])]);
        addToast("success", "Availability saved");
      }
    } catch (err) {
      console.error("Error updating availability:", err);
      addToast("error", err.message || "Something went wrong while updating availability.");
    } finally {
      setPendingGigId(null);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Header
        currentUser={currentUser}
        isArtistUser={isArtistUser}
        isVenueUser={isVenueUser}
        theme={theme}
        onLogout={handleLogout}
        onSwitchAuthMode={switchAuthMode}
        onThemeToggle={handleThemeToggle}
      />

      {/* Auth modal */}
      {showAuthModal && !isAuthenticated && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowAuthModal(false); }}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowAuthModal(false)} />
          <div className="relative z-10 w-full max-w-lg">
            <AuthForm
              authMode={authMode}
              authForm={authForm}
              isSubmittingAuth={isSubmittingAuth}
              isAuthLoading={isAuthLoading}
              onSubmit={handleAuthSubmit}
              onInputChange={handleAuthInputChange}
              onSwitchMode={switchAuthMode}
              onGoogleLogin={handleGoogleLogin}
              onClose={() => setShowAuthModal(false)}
            />
          </div>
        </div>
      )}

      {/* New Google OAuth user — pick account type */}
      {showNewUserTypeModal && (
        <NewUserTypeModal onSelect={handleSetUserType} />
      )}

      <main className="min-h-screen pb-16 pt-16">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-4 sm:px-6 lg:px-8">

          {/* Hero — unauthenticated only */}
          {!isAuthenticated && !isAuthLoading && (
            <section className="relative overflow-hidden rounded-2xl text-center">
              {/* Slideshow background */}
              <div className="absolute inset-0 z-0">
                {[
                  "https://images.unsplash.com/photo-1540039155733-5bb30b99e96e?auto=format&fit=crop&w=1600&q=80",
                  "https://images.unsplash.com/photo-1501386761578-eac5c94b800a?auto=format&fit=crop&w=1600&q=80",
                  "https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?auto=format&fit=crop&w=1600&q=80",
                  "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?auto=format&fit=crop&w=1600&q=80",
                ].map((src, i) => (
                  <div
                    key={src}
                    className="hero-slide absolute inset-0 bg-cover bg-center"
                    style={{
                      backgroundImage: `url(${src})`,
                      animationDelay: `${i * 6}s`,
                    }}
                  />
                ))}
                {/* Dark overlay — heavier at top/bottom for text legibility */}
                <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/80 via-zinc-950/65 to-zinc-950/80" />
              </div>

              {/* Content */}
              <div className="relative z-10 px-6 py-12 lg:py-16">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-400 mb-5">
                  Music booking platform
                </p>
                <h1 className="text-4xl sm:text-5xl lg:text-[3.75rem] font-bold tracking-tight text-white leading-[1.08]">
                  Where artists<br className="hidden sm:inline" /> meet venues.
                </h1>
                <p className="mt-5 text-base sm:text-lg text-white/60 max-w-md mx-auto leading-relaxed">
                  Browse open gigs, mark your availability, and get booked — all in one place.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                  <button
                    type="button"
                    className="btn-base btn-primary px-7 py-2.5 text-sm"
                    onClick={() => switchAuthMode("register")}
                  >
                    Get started
                  </button>
                  <a
                    href="#browse"
                    className="btn-base inline-flex items-center justify-center px-7 py-2.5 text-sm rounded-lg border border-white/20 text-white/80 hover:bg-white/10 transition-colors"
                  >
                    Browse open gigs
                  </a>
                </div>
                {gigs.length > 0 && (
                  <p className="mt-8 text-xs text-white/35">
                    {gigs.length} {gigs.length === 1 ? "gig" : "gigs"} available right now
                  </p>
                )}
              </div>
            </section>
          )}

          {/* Dashboard summary — authenticated only */}
          {isAuthenticated && (
            <section className="panel p-6">
              <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-400 dark:text-zinc-500">
                    {isVenueUser ? "Venue" : "Artist"}
                  </p>
                  <h1 className="mt-0.5 text-2xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50">
                    {isVenueUser ? "Booking dashboard" : "Your opportunities"}
                  </h1>
                </div>
                <dl className="flex flex-wrap gap-6">
                  {isVenueUser ? (
                    <>
                      {summary.total > 0 && (
                        <div>
                          <dt className="text-[11px] uppercase tracking-[0.1em] text-zinc-400 dark:text-zinc-500">Gigs posted</dt>
                          <dd className="mt-0.5 text-2xl font-bold text-zinc-900 dark:text-zinc-50">{summary.total}</dd>
                        </div>
                      )}
                      {sentContacts.length > 0 && (
                        <div>
                          <dt className="text-[11px] uppercase tracking-[0.1em] text-zinc-400 dark:text-zinc-500">Conversations</dt>
                          <dd className="mt-0.5 text-2xl font-bold text-zinc-900 dark:text-zinc-50">{sentContacts.length}</dd>
                        </div>
                      )}
                      {summary.nextGigDisplay && (
                        <div>
                          <dt className="text-[11px] uppercase tracking-[0.1em] text-zinc-400 dark:text-zinc-500">Next gig</dt>
                          <dd className="mt-0.5 text-base font-semibold text-zinc-900 dark:text-zinc-50">{summary.nextGigDisplay}</dd>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {summary.total > 0 && (
                        <div>
                          <dt className="text-[11px] uppercase tracking-[0.1em] text-zinc-400 dark:text-zinc-500">Open gigs</dt>
                          <dd className="mt-0.5 text-2xl font-bold text-zinc-900 dark:text-zinc-50">{summary.total}</dd>
                        </div>
                      )}
                      {summary.interested > 0 && (
                        <div>
                          <dt className="text-[11px] uppercase tracking-[0.1em] text-zinc-400 dark:text-zinc-500">Marked available</dt>
                          <dd className="mt-0.5 text-2xl font-bold text-amber-500">{summary.interested}</dd>
                        </div>
                      )}
                      {summary.nextGigDisplay && (
                        <div>
                          <dt className="text-[11px] uppercase tracking-[0.1em] text-zinc-400 dark:text-zinc-500">Next gig</dt>
                          <dd className="mt-0.5 text-base font-semibold text-zinc-900 dark:text-zinc-50">{summary.nextGigDisplay}</dd>
                        </div>
                      )}
                    </>
                  )}
                </dl>
              </div>
              {isVenueUser && (
                <div className="mt-5 flex">
                  <div className="inline-flex items-center gap-0.5 rounded-lg border border-zinc-200/70 bg-white/80 p-0.5 text-sm dark:border-white/[0.07] dark:bg-white/[0.04]">
                    <button
                      type="button"
                      className={`rounded-md px-4 py-1.5 text-sm font-medium transition ${activeView === "gigs" ? "bg-white text-zinc-900 shadow-sm dark:bg-white/[0.08] dark:text-zinc-100" : "text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"}`}
                      onClick={() => setActiveView("gigs")}
                    >
                      My gigs
                    </button>
                    <button
                      type="button"
                      className={`rounded-md px-4 py-1.5 text-sm font-medium transition ${activeView === "artists" ? "bg-white text-zinc-900 shadow-sm dark:bg-white/[0.08] dark:text-zinc-100" : "text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"}`}
                      onClick={() => setActiveView("artists")}
                    >
                      Browse artists
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}

          {isArtistUser && (
            <ArtistProfileForm
              profileForm={profileForm}
              isProfileDirty={isProfileDirty}
              isSavingProfile={isSavingProfile}
              isProfileLoading={isProfileLoading}
              artistProfile={artistProfile}
              profileGenresList={profileGenresList}
              profileInstrumentsList={profileInstrumentsList}
              profileLinksList={profileLinksList}
              onSubmit={handleProfileSubmit}
              onFieldChange={handleProfileFieldChange}
            />
          )}

          {isArtistUser && (
            <ArtistInbox
              contactRequests={contactRequests}
              isContactsLoading={isContactsLoading}
              onMarkRead={handleMarkContactRead}
              onOpenThread={openThread}
            />
          )}

          {isVenueUser && (
            <GigCreationForm
              gigForm={gigForm}
              isCreatingGig={isCreatingGig}
              editingGig={editingGig}
              onSubmit={handleGigSubmit}
              onFieldChange={handleGigFieldChange}
              onCancelEdit={handleCancelEditGig}
            />
          )}

          {isVenueUser && (
            <VenueOutbox
              sentContacts={sentContacts}
              isSentLoading={isSentLoading}
              outgoingUnread={outgoingUnread}
              onOpenThread={openThread}
              getContactPeerLabel={getContactPeerLabel}
            />
          )}

          {showGigInterface && (
            <GigBrowseSection
              filters={filters}
              cityOptions={cityOptions}
              isUsingFallback={isUsingFallback}
              displayedGigs={displayedGigs}
              isLoading={isLoading}
              activeTab={activeTab}
              canViewAvailability={canViewAvailability}
              canManageAvailability={canManageAvailability}
              myAvailabilityGigs={myAvailabilityGigs}
              interestedIds={interestedIds}
              pendingGigId={pendingGigId}
              isVenueUser={isVenueUser}
              currentUserId={currentUser?.id}
              onFilterChange={handleFilterChange}
              onResetFilters={handleResetFilters}
              onTabChange={setActiveTab}
              onViewDetails={setActiveGigId}
              onToggleAvailability={handleToggleAvailability}
              onOpenInterestedModal={openInterestedModal}
              onEditGig={handleStartEditGig}
              onDeleteGig={handleDeleteGig}
              onRequestAuth={() => switchAuthMode("login")}
            />
          )}
        </div>
      </main>

      {showArtistInterface && (
        <ArtistBrowseSection
          filteredArtists={filteredArtists}
          isArtistsLoading={isArtistsLoading}
          artistSearch={artistSearch}
          artistGenreFilter={artistGenreFilter}
          onArtistSearchChange={setArtistSearch}
          onGenreFilterChange={setArtistGenreFilter}
          onOpenArtistContact={handleOpenArtistContact}
          onViewArtistProfile={handleOpenArtistProfile}
        />
      )}

      <GigDetailsModal
        isClient={isClient}
        activeGig={activeGig}
        activeCity={activeCity}
        activePayout={activePayout}
        activeGenres={activeGenres}
        activeTags={activeTags}
        activeDescription={activeDescription}
        activeVenueName={activeVenueName}
        activeContactName={activeContactName}
        activeContactEmail={activeContactEmail}
        activeContactPhone={activeContactPhone}
        activeWebsite={activeWebsite}
        mapsUrl={mapsUrl}
        modalAvailabilityClass={modalAvailabilityClass}
        modalAvailabilityLabel={modalAvailabilityLabel}
        modalAvailabilityDisabled={modalAvailabilityDisabled}
        modalShowInterestedButton={modalShowInterestedButton}
        modalCanRespond={modalCanRespond}
        onClose={() => setActiveGigId(null)}
        onToggleAvailability={handleToggleAvailability}
        onOpenInterestedModal={openInterestedModal}
        onRequestAuth={() => switchAuthMode("login")}
      />

      <InterestedArtistsModal
        isClient={isClient}
        interestedModalGig={interestedModalGig}
        interestedModalArtists={interestedModalArtists}
        isInterestedModalLoading={isInterestedModalLoading}
        onClose={closeInterestedModal}
        onViewProfile={handleOpenArtistProfile}
        onOpenArtistContact={handleOpenArtistContact}
      />

      <ContactArtistModal
        isClient={isClient}
        activeArtistContact={activeArtistContact}
        contactMessage={contactMessage}
        isSendingContact={isSendingContact}
        onMessageChange={setContactMessage}
        onSend={handleSendArtistContact}
        onClose={handleCloseArtistContact}
      />

      <ArtistProfileModal
        isClient={isClient}
        artist={viewingArtistProfile}
        onContact={handleOpenArtistContact}
        onClose={handleCloseArtistProfile}
      />

      <ThreadModal
        isClient={isClient}
        activeThreadId={activeThreadId}
        threadMessages={threadMessages}
        threadDraft={threadDraft}
        isPostingThreadMessage={isPostingThreadMessage}
        isThreadLoading={isThreadLoading}
        threadBooking={threadBooking}
        showBookingForm={showBookingForm}
        bookingForm={bookingForm}
        isBookingAction={isBookingAction}
        currentUser={currentUser}
        canCreateBooking={canCreateBooking}
        canAcceptBooking={canAcceptBooking}
        canDeclineBooking={canDeclineBooking}
        canSignContract={canSignContract}
        canPayDeposit={canPayDeposit}
        canCancelBooking={canCancelBooking}
        canReopenBooking={canReopenBooking}
        canCounterOffer={canCounterOffer}
        venueSigned={venueSigned}
        artistSigned={artistSigned}
        depositPaid={depositPaid}
        depositAmount={depositAmount}
        showCounterForm={showCounterForm}
        counterForm={counterForm}
        onClose={closeThread}
        onSendMessage={sendThreadMessage}
        onDraftChange={setThreadDraft}
        onBookingFieldChange={handleBookingFieldChange}
        onCreateBookingOffer={createBookingOffer}
        onRespondToBooking={respondToBooking}
        onSignContract={signBookingContract}
        onPayDeposit={createDepositPayment}
        onSetShowBookingForm={setShowBookingForm}
        onOpenCounterForm={openCounterForm}
        onSetShowCounterForm={setShowCounterForm}
        onCounterFieldChange={handleCounterFieldChange}
        onSubmitCounterOffer={submitCounterOffer}
      />

      <ConfirmDialog
        isClient={isClient}
        message={confirmDialog?.message}
        confirmLabel={confirmDialog?.confirmLabel}
        isDanger={confirmDialog?.isDanger}
        onConfirm={() => { confirmDialog?.onConfirm?.(); setConfirmDialog(null); }}
        onCancel={() => setConfirmDialog(null)}
      />

      <ToastContainer
        isClient={isClient}
        toasts={toasts}
        onRemoveToast={removeToast}
      />
    </>
  );
};

export default App;
